/**
 * AppPage — 표준 3단 페이지 쉘 (검색 + 툴바 + 그리드).
 *
 * 【역할】
 * - CRUD 페이지의 상단-중단-하단 레이아웃을 한 번에 조립.
 * - SearchPanel / CrudToolbar / 그리드 본문(children) 을 `fw-page-*` 클래스와 flex 컬럼으로 정렬.
 *
 * 【주요 기능】
 * - search prop 이 있을 때만 상단 SearchPanel 렌더 (Form.Item 들을 content 로 주입).
 * - toolbar prop 은 ReactNode 또는 CrudToolbarProps 를 받아 자동 분기 렌더.
 * - gridStyle / style / className 으로 외곽/내부 스타일 오버라이드 가능.
 *
 * 【Props】
 * - search?: AppPageSearchConfig — 검색 설정(content/form/onSearch/onReset/loading/title/initialCollapsed)
 * - toolbar?: ReactNode | CrudToolbarProps — 툴바 (객체 전달 시 자동 조립)
 * - gridStyle?: CSSProperties — 그리드 영역 스타일
 * - style?: CSSProperties — 쉘 외곽 스타일
 * - className?: string — 추가 클래스
 * - children: ReactNode — 그리드 본문 (DataTable 등)
 *
 * 【사용 예시】
 * ```tsx
 * <AppPage
 *   search={{ content: <SearchFields />, form, onSearch, loading }}
 *   toolbar={{ onAdd, onDelete, dirty }}
 * >
 *   <DataTable ... />
 * </AppPage>
 * ```
 */
import { isValidElement, type CSSProperties, type ReactNode } from 'react'
import type { FormInstance } from 'antd'
import SearchPanel from './SearchPanel'
import CrudToolbar, { type CrudToolbarProps } from './CrudToolbar'

export interface AppPageSearchConfig {
  /** Form.Item 들 — SearchPanel 내부 children 으로 전달. */
  content: ReactNode
  /** 외부 제어용 Form 인스턴스. 생략 시 SearchPanel 이 내부 form 생성. */
  form?: FormInstance
  /** 검색 버튼 클릭 핸들러. */
  onSearch?: () => void
  /** 초기화 버튼 클릭 핸들러. */
  onReset?: () => void
  /** 검색 버튼 로딩 상태. */
  loading?: boolean
  /** 패널 타이틀 (기본: LBL_SEARCH_PANEL). */
  title?: string
  /** 초기 접힘 여부 (기본 false). */
  initialCollapsed?: boolean
}

export interface AppPageProps {
  /** 검색 영역. 생략 시 검색 섹션 미렌더. */
  search?: AppPageSearchConfig
  /** 도구 모음. CrudToolbarProps 객체(자동 조립) 또는 임의 ReactNode. 생략 시 툴바 미렌더. */
  toolbar?: ReactNode | CrudToolbarProps
  /** grid 영역 커스텀 스타일. */
  gridStyle?: CSSProperties
  /** 쉘 전체 스타일 오버라이드. */
  style?: CSSProperties
  /** 외곽 div 에 추가할 className. */
  className?: string
  /** 그리드 본문 (DataTable 등). */
  children: ReactNode
}

/**
 * ReactNode 판별 가드.
 * - null/undefined, primitive(string/number/boolean), Array, ReactElement 모두 ReactNode 로 인정.
 * - toolbar prop 의 분기 처리에서 "이 값을 그대로 렌더해도 되는가" 판단에 사용.
 */
function isReactNode(x: unknown): x is ReactNode {
  if (x == null) return true
  if (typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') return true
  if (Array.isArray(x)) return true
  return isValidElement(x)
}

/**
 * CrudToolbarProps 판별 가드 (ReactElement 가 아닌 plain object 인지 확인).
 * - 객체이면서 ReactElement 가 아니면 CrudToolbar 로 spread 조립할 수 있는 prop bag 으로 간주.
 */
function isCrudToolbarProps(x: unknown): x is CrudToolbarProps {
  if (!x || typeof x !== 'object') return false
  if (isValidElement(x)) return false
  return true
}

/**
 * AppPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function AppPage({
  search,
  toolbar,
  gridStyle,
  style,
  className,
  children
}: AppPageProps) {
  const shellClassName = ['fw-page', 'fw-page-shell', className].filter(Boolean).join(' ')

  const toolbarNode = (() => {
    if (toolbar == null) return null
    if (isValidElement(toolbar)) return toolbar
    if (isCrudToolbarProps(toolbar) && !isReactNode(toolbar)) return <CrudToolbar {...toolbar} />
    // 기본: CrudToolbarProps 로 가정하고 조립. ReactNode(문자열/숫자 등) 는 그대로 렌더.
    if (typeof toolbar === 'object' && !Array.isArray(toolbar)) {
      return <CrudToolbar {...(toolbar as CrudToolbarProps)} />
    }
    return toolbar as ReactNode
  })()

  return (
    <div className={shellClassName} style={style}>
      {search && (
        <div className="fw-page-search">
          <SearchPanel
            title={search.title}
            form={search.form}
            onSearch={search.onSearch}
            onReset={search.onReset}
            loading={search.loading}
            initialCollapsed={search.initialCollapsed}
          >
            {search.content}
          </SearchPanel>
        </div>
      )}
      {toolbarNode && <div className="fw-page-toolbar">{toolbarNode}</div>}
      <div className="fw-page-grid" style={gridStyle}>
        {children}
      </div>
    </div>
  )
}
